import { Children, Fragment, ReactNode } from "react";
import { SchedulerHeader, SchedulerHeaderProps } from "@progress/kendo-react-scheduler";

interface SchedulerContextHeaderProps extends SchedulerHeaderProps {
  name?: string;
  location?: string;
  /** Rendered in the toolbar after the name/location chip. */
  extraChip?: ReactNode;
}

const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0].toUpperCase())
    .join("");

export const SchedulerContextHeader = (props: SchedulerContextHeaderProps) => {
  const { name, location, extraChip, children, ...headerProps } = props;

  if (!name && !extraChip) {
    return <SchedulerHeader {...headerProps}>{children}</SchedulerHeader>;
  }

  const chip = name && (
    <div
      key="scheduler-context-chip"
      className="d-flex align-items-center gap-2 ms-2 px-2 py-1 border rounded-pill bg-light"
      style={{ maxWidth: "40%" }}
      title={location ? `${name} · ${location}` : name}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold flex-shrink-0"
        style={{ width: 24, height: 24, fontSize: 11, backgroundColor: "var(--bs-primary)" }}
      >
        {getInitials(name)}
      </span>
      <span className="fw-semibold text-truncate" style={{ minWidth: 0 }}>
        {name}
      </span>
      {location && (
        <>
          <span className="text-muted flex-shrink-0">·</span>
          <span className="text-muted small text-truncate" style={{ minWidth: 0 }}>
            {location}
          </span>
        </>
      )}
    </div>
  );


  const injected = [chip, extraChip].filter(Boolean) as ReactNode[];

  const items = Children.toArray(children);
  const insertAt = Math.min(2, items.length);
  const content = [
    ...items.slice(0, insertAt),
    ...injected.map((node, index) => (
      <Fragment key={`scheduler-header-chip-${index}`}>{node}</Fragment>
    )),
    ...items.slice(insertAt),
  ];

  return <SchedulerHeader {...headerProps}>{content}</SchedulerHeader>;
};

export default SchedulerContextHeader;
